import React, { useMemo, useState } from 'react';

/**
 * DashboardCampaignHeader — la barre de filtres d'un dashboard.
 *
 * Reprise de celle de Microsoft Ads (`App.js`, section « Filtre par campagne Microsoft
 * Ads ») : barre collante crème, logo de la plateforme devant le libellé, champ de
 * recherche de 400 px avec sa croix d'effacement et sa liste déroulante, actions alignées
 * à droite.
 *
 * ## Pourquoi un composant à part plutôt qu'une barre dans la vue
 *
 * La version Microsoft vit dans un `App.js` de 42 000 lignes, dont elle n'est pas
 * extractible sans y toucher largement. La recopier dans chaque régie, c'est s'obliger à
 * répercuter à la main la moindre correction — et ces barres se ressemblent trop pour que
 * l'oubli se voie. Seul ChatGPT Ads s'en sert aujourd'hui ; Microsoft Ads garde sa copie en
 * place et pourra la rejoindre sans que ce composant change.
 *
 * ## Le contrat d'une option
 *
 * `options` porte des triplets `{ cle, libelle, valeur }`. Trois champs et non un seul,
 * parce qu'une régie ne filtre pas forcément sur ce qu'elle affiche : ChatGPT Ads a besoin
 * de l'objet campagne entier, son backend lisant `campaignFilter.id`, alors que la liste
 * montre un nom. `cle` sert à la sélection et aux clés React, `libelle` à l'affichage,
 * `valeur` est ce qui remonte à `onSelection`.
 */
const DashboardCampaignHeader = ({
  logo,
  logoAlt = '',
  /** Le libellé devant le champ. Dit **ce que** le filtre restreint, pas seulement qu'il filtre. */
  libelle = 'Filtrer:',
  /** Ce qui s'affiche quand il n'y a rien à filtrer — le nom de la plateforme, en général. */
  libelleSansOptions = '',
  options = [],
  /** La `cle` de l'option retenue, ou `null`. */
  selection = null,
  onSelection,
  placeholder = 'Rechercher une campagne...',
  /** Rendu à droite : bouton d'analyse, lien « Modules masqués »… */
  actions = null
}) => {
  const [recherche, setRecherche] = useState('');
  const [listeOuverte, setListeOuverte] = useState(false);

  const filtrees = useMemo(() => {
    const terme = recherche.toLowerCase().trim();
    const retenues = terme
      ? options.filter((o) => String(o.libelle || '').toLowerCase().includes(terme))
      : options;
    return [...retenues].sort((a, b) => String(a.libelle).localeCompare(String(b.libelle)));
  }, [options, recherche]);

  /*
   * Le libellé de la sélection est cherché dans les options, et la `cle` sert de repli.
   *
   * Une campagne retenue puis disparue de la période garderait son nom affiché si on le
   * mémorisait à part — l'écran annoncerait un filtre sur une campagne qui n'est plus
   * dans la liste. Le repli sur la clé est brut mais vrai.
   */
  const libelleSelection = useMemo(() => {
    if (!selection) return '';
    return options.find((o) => o.cle === selection)?.libelle || selection;
  }, [options, selection]);

  const aDesOptions = options.length > 0;

  return (
    <div style={{
      position: 'sticky',
      top: '0',
      zIndex: '1000',
      background: '#FAF9F5',
      borderRadius: '8px',
      padding: '16px',
      marginTop: '-5px',
      marginBottom: '10px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{
            fontWeight: '500',
            color: '#374151',
            fontSize: '0.95em',
            display: 'flex',
            alignItems: 'center'
          }}>
            {logo && (
              <img
                src={logo}
                alt={logoAlt}
                style={{ width: '24px', height: '24px', marginRight: '8px', objectFit: 'contain', borderRadius: '4px' }}
              />
            )}
            {aDesOptions ? libelle : libelleSansOptions}
          </span>

          {aDesOptions && (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <input
                type="text"
                placeholder={placeholder}
                value={selection ? libelleSelection : recherche}
                onChange={(e) => {
                  if (selection) onSelection(null);
                  setRecherche(e.target.value);
                  setListeOuverte(true);
                }}
                onFocus={() => setListeOuverte(true)}
                // Le délai laisse passer le clic sur une option, qui sans lui démonterait
                // la liste avant d'être enregistré.
                onBlur={() => setTimeout(() => setListeOuverte(false), 200)}
                style={{
                  padding: '4px 12px',
                  paddingRight: (recherche || selection) ? '28px' : '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: selection ? '#dbeafe' : 'white',
                  color: '#374151',
                  fontSize: '0.9em',
                  width: '400px',
                  height: '35px',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
              {(recherche || selection) && (
                <button
                  onClick={() => {
                    setRecherche('');
                    onSelection(null);
                    setListeOuverte(false);
                  }}
                  style={{
                    position: 'absolute',
                    right: '6px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: selection ? '#1a1a1a' : '#9ca3af',
                    padding: '2px',
                    fontSize: '14px',
                    lineHeight: 1,
                    zIndex: 1
                  }}
                  title={selection ? 'Effacer le filtre campagne' : 'Effacer la recherche'}
                >
                  ✕
                </button>
              )}

              {listeOuverte && (
                filtrees.length === 0 ? (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    marginTop: '4px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    zIndex: 1001,
                    padding: '12px',
                    color: '#6b7280',
                    fontSize: '0.9em'
                  }}>
                    Aucune campagne trouvée
                  </div>
                ) : (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    marginTop: '4px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    zIndex: 1001,
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    <div style={{
                      padding: '8px 12px',
                      fontSize: '0.8em',
                      color: '#6b7280',
                      borderBottom: '1px solid #e5e7eb',
                      backgroundColor: '#f9fafb'
                    }}>
                      {recherche.trim()
                        ? `${filtrees.length} campagne(s) trouvée(s)`
                        : `Toutes les campagnes (${filtrees.length})`}
                    </div>
                    {filtrees.map((option, index) => (
                      <div
                        key={option.cle}
                        onClick={() => {
                          onSelection(option.valeur);
                          setRecherche('');
                          setListeOuverte(false);
                        }}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          fontSize: '0.9em',
                          color: '#374151',
                          borderBottom: index < filtrees.length - 1 ? '1px solid #f3f4f6' : 'none',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => { e.target.style.backgroundColor = '#f3f4f6'; }}
                        onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; }}
                      >
                        {option.libelle}
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {actions}
        </div>
      </div>
    </div>
  );
};

export default DashboardCampaignHeader;
